import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { 
  History, 
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Clock,
  Trash2,
  RotateCcw,
  User,
  Bot
} from 'lucide-react';

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  feedback?: 'positive' | 'negative';
  references?: string[];
  followUpQuestions?: string[];
}

interface ChatSession {
  id: string;
  title: string;
  timestamp: Date;
  messages: Message[];
  messageCount: number;
}

interface SessionMemoryProps {
  onRestoreSession: (messages: Message[]) => void;
  currentMessages: Message[];
  onNewSession: () => void;
}

// Pre-existing mock sessions with realistic conversations
const mockSessions: ChatSession[] = [
  {
    id: 'session-1',
    title: 'Time Off Request & Benefits',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
    messageCount: 8,
    messages: [
      {
        id: '1',
        type: 'bot',
        content: "Hello! I'm your AskHR assistant. I can help you with company policies, benefits, time off requests, payroll questions, and more. What would you like to know?",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        references: [],
        followUpQuestions: [
          "What are the most common HR questions?",
          "How do I access my employee benefits?",
          "What should I know as a new employee?"
        ]
      },
      {
        id: '2',
        type: 'user',
        content: "Hi! I need to request time off for a family vacation next month. How do I do that?",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 1000),
      },
      {
        id: '3',
        type: 'bot',
        content: "I can help you with your time off request! You can submit vacation requests through the employee portal. Here's what you need to know:\n\n• Submit requests at least 2 weeks in advance\n• Include the dates and reason for your request\n• Your manager will review and approve within 3 business days\n• You can check the status in your portal dashboard\n\nWould you like me to show you how to access the time off request form?",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 2000),
        references: ['Time Off Policy', 'Employee Portal Guide'],
        followUpQuestions: [
          "How much vacation time do I have available?",
          "What if my request is denied?",
          "Can I request time off for medical appointments?"
        ]
      },
      {
        id: '4',
        type: 'user',
        content: "Yes, please show me how to access the form. Also, how much vacation time do I have left?",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 3000),
      },
      {
        id: '5',
        type: 'bot',
        content: "Great! You can access the time off request form by:\n\n1. Going to the 'Time Off & Scheduling' section in your portal\n2. Clicking 'Request Time Off'\n3. Filling out the form with your dates and reason\n\nRegarding your vacation balance: You currently have 12 days of vacation time remaining for this year. You also have 3 personal days available.\n\nYour vacation time resets on January 1st, and any unused time will be paid out if you leave the company.",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 4000),
        references: ['Time Off Request Form', 'Vacation Policy', 'Personal Leave Policy'],
        followUpQuestions: [
          "What's the difference between vacation and personal days?",
          "Can I carry over unused vacation to next year?",
          "How do I check my time off balance?"
        ]
      },
      {
        id: '6',
        type: 'user',
        content: "What's the difference between vacation and personal days?",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 5000),
      },
      {
        id: '7',
        type: 'bot',
        content: "Here's the difference between vacation and personal days:\n\n**Vacation Days:**\n• 15 days per year (accrued monthly)\n• Can be used for any purpose\n• Must be requested in advance\n• Unused time is paid out if you leave\n\n**Personal Days:**\n• 3 days per year\n• Can be used for emergencies or short notice\n• More flexible scheduling\n• Do not carry over to next year\n\nPersonal days are great for last-minute appointments or unexpected events, while vacation days are for planned time off.",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 6000),
        references: ['Vacation Policy', 'Personal Leave Policy'],
        followUpQuestions: [
          "Can I use personal days for doctor appointments?",
          "What if I need more time off than I have available?",
          "How do I request sick leave?"
        ]
      },
      {
        id: '8',
        type: 'user',
        content: "Perfect, thank you! I'll submit my vacation request now.",
        timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000 + 7000),
      }
    ]
  },
  {
    id: 'session-2',
    title: 'Health Benefits & Insurance',
    timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
    messageCount: 6,
    messages: [
      {
        id: '1',
        type: 'bot',
        content: "Hello! I'm your AskHR assistant. I can help you with company policies, benefits, time off requests, payroll questions, and more. What would you like to know?",
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        references: [],
        followUpQuestions: [
          "What are the most common HR questions?",
          "How do I access my employee benefits?",
          "What should I know as a new employee?"
        ]
      },
      {
        id: '2',
        type: 'user',
        content: "I'm a new employee and I need to understand my health insurance options. What plans are available?",
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000 + 1000),
      },
      {
        id: '3',
        type: 'bot',
        content: "Welcome to the team! We offer comprehensive health insurance options:\n\n**Medical Plans:**\n• PPO Plan: $50/month premium, $500 deductible\n• HMO Plan: $30/month premium, $750 deductible\n• High Deductible Plan: $20/month premium, $2000 deductible\n\n**Dental & Vision:**\n• Basic dental: $15/month\n• Comprehensive dental: $25/month\n• Vision: $10/month\n\n**Additional Benefits:**\n• Life insurance (2x salary)\n• Disability insurance\n• Mental health coverage\n\nYou have 30 days from your start date to enroll. Would you like me to help you understand which plan might be best for you?",
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000 + 2000),
        references: ['Health Insurance Guide', 'Benefits Summary', 'Enrollment Periods'],
        followUpQuestions: [
          "How do I compare the different medical plans?",
          "What's covered under the dental plan?",
          "Can I add my family to my insurance?"
        ]
      },
      {
        id: '4',
        type: 'user',
        content: "Can I add my family to my insurance? And what's the cost difference?",
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000 + 3000),
      },
      {
        id: '5',
        type: 'bot',
        content: "Yes, you can definitely add your family! Here are the family coverage options:\n\n**Family Coverage Costs:**\n• Employee + Spouse: 2x the individual premium\n• Employee + Child(ren): 1.5x the individual premium\n• Employee + Family: 2.5x the individual premium\n\n**Example with PPO Plan:**\n• Individual: $50/month\n• Employee + Spouse: $100/month\n• Employee + Family: $125/month\n\n**Important Notes:**\n• You can add family members during open enrollment or within 30 days of a qualifying life event\n• Dependents can stay on your plan until age 26\n• Spouse coverage ends if they get their own employer-sponsored insurance\n\nWould you like me to help you calculate the costs for your specific family situation?",
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000 + 4000),
        references: ['Family Coverage Guide', 'Premium Calculator', 'Qualifying Life Events'],
        followUpQuestions: [
          "What counts as a qualifying life event?",
          "How do I enroll my family members?",
          "What if my spouse has their own insurance?"
        ]
      },
      {
        id: '6',
        type: 'user',
        content: "Thanks! I'll review the plans and enroll within the 30-day window.",
        timestamp: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000 + 5000),
      }
    ]
  }
];

export function SessionMemory({ onRestoreSession, currentMessages, onNewSession }: SessionMemoryProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Load sessions from localStorage on component mount, or initialize with mock sessions
  useEffect(() => {
    const savedSessions = localStorage.getItem('askhr-sessions');
    if (savedSessions) {
      try {
        const parsedSessions = JSON.parse(savedSessions).map((session: any) => ({
          ...session,
          timestamp: new Date(session.timestamp),
          messages: session.messages.map((msg: any) => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }))
        }));
        setSessions(parsedSessions);
      } catch (error) {
        console.error('Error loading sessions:', error);
        // If there's an error loading saved sessions, initialize with mock sessions
        setSessions(mockSessions);
        localStorage.setItem('askhr-sessions', JSON.stringify(mockSessions));
      }
    } else {
      // No saved sessions, initialize with mock sessions
      setSessions(mockSessions);
      localStorage.setItem('askhr-sessions', JSON.stringify(mockSessions));
    }
  }, []);

  // Save current session when it has meaningful content and is different from existing sessions
  const saveCurrentSession = () => {
    if (currentMessages.length <= 1) return; // Don't save sessions with only welcome message

    // Check if this is a new session or an update to an existing one
    const isNewSession = !currentSessionId;
    
    if (isNewSession) {
      const newSession: ChatSession = {
        id: Date.now().toString(),
        title: generateSessionTitle(currentMessages),
        timestamp: new Date(),
        messages: currentMessages,
        messageCount: currentMessages.filter(m => m.type === 'user').length
      };

      const updatedSessions = [newSession, ...sessions].slice(0, 5); // Keep only last 5 sessions
      setSessions(updatedSessions);
      localStorage.setItem('askhr-sessions', JSON.stringify(updatedSessions));
    }
  };

  // Generate a meaningful title for the session based on the first user message
  const generateSessionTitle = (messages: Message[]): string => {
    const firstUserMessage = messages.find(m => m.type === 'user');
    if (firstUserMessage) {
      const content = firstUserMessage.content;
      if (content.length > 40) {
        return content.substring(0, 40) + '...';
      }
      return content;
    }
    return 'Chat Session';
  };

  const handleRestoreSession = (session: ChatSession) => {
    // Save current session before restoring (only if it's different)
    if (currentMessages.length > 1 && currentSessionId !== session.id) {
      saveCurrentSession();
    }
    
    setCurrentSessionId(session.id);
    onRestoreSession(session.messages);
    setIsExpanded(false);
  };

  const handleNewSession = () => {
    // Save current session before starting new one (only if it's different)
    if (currentMessages.length > 1) {
      saveCurrentSession();
    }
    
    setCurrentSessionId(null);
    onNewSession();
    setIsExpanded(false);
  };

  const handleDeleteSession = (sessionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    const updatedSessions = sessions.filter(s => s.id !== sessionId);
    setSessions(updatedSessions);
    localStorage.setItem('askhr-sessions', JSON.stringify(updatedSessions));
    
    // If we're deleting the current session, clear the current session ID
    if (currentSessionId === sessionId) {
      setCurrentSessionId(null);
    }
  };

  const formatTimestamp = (timestamp: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} min ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return timestamp.toLocaleDateString();
    }
  };

  return (
    <div className={`fixed top-0 right-0 h-full z-40 transition-all duration-300 ease-in-out ${
      isExpanded ? 'w-80' : 'w-12'
    }`}>
      {/* Collapsed State - Icon Only */}
      {!isExpanded && (
        <div className="h-full flex items-center">
          <div className="bg-white dark:bg-gray-800 border-l border-y border-gray-200 dark:border-gray-700 rounded-l-lg shadow-lg">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="h-16 w-12 rounded-none rounded-l-lg flex flex-col items-center justify-center gap-1 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              title="Open Session History"
            >
              <History className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              {sessions.length > 0 && (
                <Badge variant="secondary" className="h-4 w-4 p-0 text-xs rounded-full">
                  {sessions.length}
                </Badge>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Extended State - Full Panel */}
      {isExpanded && (
        <div className="h-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Session History
              </h3>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(false)}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* New Session Button */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <Button
              onClick={handleNewSession}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              size="sm"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              New Chat
            </Button>
          </div>

          {/* Sessions List */}
          <div className="flex-1 overflow-hidden">
            {sessions.length === 0 ? (
              <div className="p-4 text-center">
                <div className="text-gray-500 dark:text-gray-400 mb-2">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No previous sessions</p>
                  <p className="text-xs">Your chat history will appear here</p>
                </div>
              </div>
            ) : (
              <ScrollArea className="h-full">
                <div className="p-2 space-y-2">
                  {sessions.map((session) => (
                    <Card
                      key={session.id}
                      className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-gray-200 dark:border-gray-700 ${
                        currentSessionId === session.id ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                      onClick={() => handleRestoreSession(session)}
                    >
                      <CardContent className="p-3">
                        <div className="space-y-2">
                          {/* Session Header */}
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-tight">
                              {session.title}
                            </h4>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => handleDeleteSession(session.id, e)}
                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-500 flex-shrink-0"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>

                          {/* Session Metadata */}
                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {formatTimestamp(session.timestamp)}
                            </div>
                            <div className="flex items-center gap-1">
                              <MessageSquare className="h-3 w-3" />
                              {session.messageCount} messages
                            </div>
                          </div>

                          {/* Preview of last messages */}
                          <div className="space-y-1">
                            {session.messages.slice(-2).map((message, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <div className="flex-shrink-0">
                                  {message.type === 'user' ? (
                                    <User className="h-3 w-3 text-purple-500 mt-0.5" />
                                  ) : (
                                    <Bot className="h-3 w-3 text-blue-500 mt-0.5" />
                                  )}
                                </div>
                                <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 leading-tight">
                                  {message.content.length > 60 
                                    ? message.content.substring(0, 60) + '...'
                                    : message.content
                                  }
                                </p>
                              </div>
                            ))}
                          </div>

                          {/* Restore indicator */}
                          <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 pt-1 border-t border-gray-100 dark:border-gray-600">
                            <RotateCcw className="h-3 w-3" />
                            {currentSessionId === session.id ? 'Current session' : 'Click to restore session'}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Last {sessions.length}/5 sessions saved
            </p>
          </div>
        </div>
      )}
    </div>
  );
}